import { arrayMove } from "@dnd-kit/sortable"

import type { DashboardCardType, DashboardLayoutItem } from "../../../../types/dashboardLayout"
import {
  buildDashboardChartRows,
  coalesceSplitChartRows,
  isSplittableChartRowCard,
} from "./dashboardChartRows"
import { isMiniStatSlotId, parseMiniStatSlotId } from "../components/dashboardLayoutUi"

const SPLIT_ROW_CARD_TYPES: DashboardCardType[] = [
  "portfolioGrowth",
  "winRateTrend",
  "pnlMilestones",
]

export type DashboardLayoutMoveDirection = "up" | "down"

export interface DashboardLayoutMoveState {
  canMoveUp: boolean
  canMoveDown: boolean
}

function getVisibleMiniStatItems(items: DashboardLayoutItem[]): DashboardLayoutItem[] {
  return items
    .filter((item) => item.zone === "miniStats" && item.visible)
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
}

function getSplitRowSortableIds(items: DashboardLayoutItem[]): DashboardCardType[] {
  return items
    .filter(
      (item) =>
        item.zone === "splitRow" &&
        item.visible &&
        SPLIT_ROW_CARD_TYPES.includes(item.cardType)
    )
    .sort((a, b) => {
      if (a.slot === "left" && b.slot !== "left") return -1
      if (b.slot === "left" && a.slot !== "left") return 1
      return (a.order ?? 0) - (b.order ?? 0)
    })
    .map((item) => item.cardType)
}

function rebuildSplittableChartRowGroups(
  items: DashboardLayoutItem[],
  groups: DashboardCardType[][]
): DashboardLayoutItem[] {
  const placement = new Map<DashboardCardType, { order: number; slot?: "left" | "right" }>()
  groups.forEach((group, order) => {
    if (group.length >= 2) {
      placement.set(group[0], { order, slot: "left" })
      placement.set(group[1], { order, slot: "right" })
      return
    }
    if (group.length === 1) {
      placement.set(group[0], { order })
    }
  })

  return coalesceSplitChartRows(
    items.map((item) => {
      const nextPlacement = placement.get(item.cardType)
      if (!nextPlacement) return item
      return {
        ...item,
        zone: "fullWidth" as const,
        order: nextPlacement.order,
        slot: nextPlacement.slot,
        ratio: nextPlacement.slot ? 0.5 : undefined,
      }
    })
  )
}

function getSplittableChartRowGroups(items: DashboardLayoutItem[]): DashboardCardType[][] {
  return buildDashboardChartRows(items).map((row) =>
    row.kind === "full" ? [row.cardType] : [row.left, row.right]
  )
}

export function moveMiniStatSlot(
  items: DashboardLayoutItem[],
  slotIndex: number,
  direction: DashboardLayoutMoveDirection
): DashboardLayoutItem[] {
  const visibleMiniStats = getVisibleMiniStatItems(items)
  const nextIndex = direction === "up" ? slotIndex - 1 : slotIndex + 1
  if (nextIndex < 0 || nextIndex >= visibleMiniStats.length) return items

  const orderedIds = visibleMiniStats.map((item) => item.cardType)
  const nextOrder = arrayMove(orderedIds, slotIndex, nextIndex)

  return items.map((item) => {
    if (item.zone !== "miniStats") return item
    const idx = nextOrder.indexOf(item.cardType)
    return { ...item, order: idx === -1 ? item.order : idx }
  })
}

export function swapSplitRowCards(
  items: DashboardLayoutItem[],
  activeId: DashboardCardType,
  overId: DashboardCardType
): DashboardLayoutItem[] {
  const activeItem = items.find((item) => item.cardType === activeId)
  const overItem = items.find((item) => item.cardType === overId)
  if (!activeItem || !overItem) return items

  return items.map((item) => {
    if (item.cardType === activeId) {
      return {
        ...item,
        slot: overItem.slot,
        order: overItem.order,
        ratio: typeof overItem.ratio === "number" ? overItem.ratio : item.ratio,
      }
    }
    if (item.cardType === overId) {
      return {
        ...item,
        slot: activeItem.slot,
        order: activeItem.order,
        ratio: typeof activeItem.ratio === "number" ? activeItem.ratio : item.ratio,
      }
    }
    return item
  })
}

export function moveSplitRowCard(
  items: DashboardLayoutItem[],
  cardType: DashboardCardType,
  direction: DashboardLayoutMoveDirection
): DashboardLayoutItem[] {
  const sortableIds = getSplitRowSortableIds(items)
  const index = sortableIds.indexOf(cardType)
  if (index === -1) return items

  const nextIndex = direction === "up" ? index - 1 : index + 1
  if (nextIndex < 0 || nextIndex >= sortableIds.length) return items

  return swapSplitRowCards(items, cardType, sortableIds[nextIndex])
}

export function moveSplittableChartRowCard(
  items: DashboardLayoutItem[],
  cardType: DashboardCardType,
  direction: DashboardLayoutMoveDirection
): DashboardLayoutItem[] {
  if (!isSplittableChartRowCard(cardType)) return items

  const groups = getSplittableChartRowGroups(items)
  let groupIndex = -1
  let cardIndex = -1

  for (let groupIdx = 0; groupIdx < groups.length; groupIdx += 1) {
    const foundIndex = groups[groupIdx].indexOf(cardType)
    if (foundIndex !== -1) {
      groupIndex = groupIdx
      cardIndex = foundIndex
      break
    }
  }

  if (groupIndex === -1) return items

  const group = groups[groupIndex]
  const peerIndex = direction === "up" ? cardIndex - 1 : cardIndex + 1

  if (group.length >= 2 && peerIndex >= 0 && peerIndex < group.length) {
    const nextGroup = [...group]
    ;[nextGroup[cardIndex], nextGroup[peerIndex]] = [nextGroup[peerIndex], nextGroup[cardIndex]]
    const nextGroups = [...groups]
    nextGroups[groupIndex] = nextGroup
    return rebuildSplittableChartRowGroups(items, nextGroups)
  }

  const targetGroupIndex = direction === "up" ? groupIndex - 1 : groupIndex + 1
  if (targetGroupIndex < 0 || targetGroupIndex >= groups.length) return items

  const nextGroups = [...groups]
  ;[nextGroups[groupIndex], nextGroups[targetGroupIndex]] = [
    nextGroups[targetGroupIndex],
    nextGroups[groupIndex],
  ]
  return rebuildSplittableChartRowGroups(items, nextGroups)
}

export function getDashboardLayoutMoveState(
  items: DashboardLayoutItem[],
  sortableId: string
): DashboardLayoutMoveState {
  if (isMiniStatSlotId(sortableId)) {
    const slotIndex = parseMiniStatSlotId(sortableId)
    const visibleCount = getVisibleMiniStatItems(items).length
    if (slotIndex === null || visibleCount <= 1) {
      return { canMoveUp: false, canMoveDown: false }
    }
    return {
      canMoveUp: slotIndex > 0,
      canMoveDown: slotIndex < visibleCount - 1,
    }
  }

  const cardType = sortableId as DashboardCardType
  const item = items.find((entry) => entry.cardType === cardType)
  if (!item?.visible) {
    return { canMoveUp: false, canMoveDown: false }
  }

  if (item.zone === "splitRow" && SPLIT_ROW_CARD_TYPES.includes(cardType)) {
    const sortableIds = getSplitRowSortableIds(items)
    const index = sortableIds.indexOf(cardType)
    return {
      canMoveUp: index > 0,
      canMoveDown: index >= 0 && index < sortableIds.length - 1,
    }
  }

  if (item.zone === "fullWidth" && isSplittableChartRowCard(cardType)) {
    const groups = getSplittableChartRowGroups(items)
    let groupIndex = -1
    let cardIndex = -1

    for (let groupIdx = 0; groupIdx < groups.length; groupIdx += 1) {
      const foundIndex = groups[groupIdx].indexOf(cardType)
      if (foundIndex !== -1) {
        groupIndex = groupIdx
        cardIndex = foundIndex
        break
      }
    }

    if (groupIndex === -1) {
      return { canMoveUp: false, canMoveDown: false }
    }

    const group = groups[groupIndex]
    return {
      canMoveUp: cardIndex > 0 || groupIndex > 0,
      canMoveDown: cardIndex < group.length - 1 || groupIndex < groups.length - 1,
    }
  }

  return { canMoveUp: false, canMoveDown: false }
}

export function applyDashboardLayoutMove(
  items: DashboardLayoutItem[],
  sortableId: string,
  direction: DashboardLayoutMoveDirection
): DashboardLayoutItem[] {
  if (isMiniStatSlotId(sortableId)) {
    const slotIndex = parseMiniStatSlotId(sortableId)
    if (slotIndex === null) return items
    return moveMiniStatSlot(items, slotIndex, direction)
  }

  const cardType = sortableId as DashboardCardType
  const item = items.find((entry) => entry.cardType === cardType)
  if (!item) return items

  if (item.zone === "splitRow" && SPLIT_ROW_CARD_TYPES.includes(cardType)) {
    return moveSplitRowCard(items, cardType, direction)
  }

  if (item.zone === "fullWidth" && isSplittableChartRowCard(cardType)) {
    return moveSplittableChartRowCard(items, cardType, direction)
  }

  return items
}
